<script setup lang="ts">
/* =============================================================
   MODAL
   =============================================================
   A native <dialog> opened with showModal(), which is why there
   is no focus-trap code, no scroll lock and no Escape handler
   here — the platform does all three, correctly, for free.

   The only things worth noticing:
     - `@cancel` covers Escape, which does NOT fire `close` on
       its own path through our button handlers.
     - the backdrop is a ::backdrop pseudo-element, so clicking
       outside is opt-in rather than automatic; `closeOnBackdrop`
       wires it up by comparing the click target to the dialog.
   ============================================================= */

import { onBeforeUnmount, ref, watch } from 'vue'
import Icon from '@/components/Icon.vue'

const props = withDefaults(
  defineProps<{
    open: boolean
    title: string
    description?: string
    /** Clicking the backdrop dismisses. Off while a save is in flight. */
    closeOnBackdrop?: boolean
  }>(),
  { closeOnBackdrop: true },
)

const emit = defineEmits<{ close: [] }>()

const dialog = ref<HTMLDialogElement | null>(null)

watch(
  () => props.open,
  (open) => {
    const el = dialog.value
    if (!el) return
    if (open && !el.open) el.showModal()
    if (!open && el.open) el.close()
  },
  // `post` so the content inside v-if has rendered before showModal() runs —
  // otherwise the dialog opens empty for a tick and initial focus lands on
  // the dialog itself rather than the first field.
  { flush: 'post' },
)

// A dialog left open in the DOM keeps the page inert if the route changes
// out from under it.
onBeforeUnmount(() => {
  if (dialog.value?.open) dialog.value.close()
})

function onBackdropClick(event: MouseEvent): void {
  if (!props.closeOnBackdrop) return
  // The dialog element's own box is the backdrop's hit area; a click that
  // lands on it rather than on the content came from outside.
  if (event.target === dialog.value) emit('close')
}
</script>

<template>
  <dialog ref="dialog" class="dialog" @cancel.prevent="emit('close')" @click="onBackdropClick">
    <div v-if="open" class="inner">
      <header class="header">
        <div class="headings">
          <h2 class="title">{{ title }}</h2>
          <p v-if="description" class="description">{{ description }}</p>
        </div>
        <button type="button" class="close" aria-label="Close" @click="emit('close')">
          <Icon name="close" :size="18" />
        </button>
      </header>

      <div class="body"><slot /></div>

      <footer v-if="$slots.footer" class="footer"><slot name="footer" /></footer>
    </div>
  </dialog>
</template>

<style scoped>
.dialog {
  width: min(32rem, calc(100vw - 2 * var(--space-sm)));
  /* The UA centres a modal <dialog> with `inset: 0; margin: auto`, and
     base.css's `* { margin: 0 }` reset silently undoes half of that. Without
     this line the dialog pins itself to the top-left corner. */
  margin: auto;
  padding: 0;
  color: var(--color-text);
  background-color: var(--color-surface);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
}

.dialog::backdrop {
  background-color: rgb(4 18 29 / 45%);
  backdrop-filter: blur(2px);
}

.dialog[open] {
  animation: rise var(--dur-base) var(--ease);
}

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(0.5rem) scale(0.98);
  }
}

@media (prefers-reduced-motion: reduce) {
  .dialog[open] {
    animation: none;
  }
}

.inner {
  display: flex;
  flex-direction: column;
  max-height: min(42rem, calc(100dvh - 2 * var(--space-lg)));
}

.header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-sm);
  padding: var(--space-md) var(--space-md) var(--space-sm);
}

.headings {
  min-width: 0;
}

.title {
  font-size: var(--text-md);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-tight);
}

.description {
  margin-top: var(--space-3xs);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.close {
  flex: none;
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  color: var(--color-text-faint);
  background: none;
  border: 0;
  border-radius: var(--radius-sm);
}

.close:hover {
  color: var(--color-text);
  background-color: var(--color-surface-sunken);
}

.body {
  padding-inline: var(--space-md);
  padding-block-end: var(--space-md);
  overflow-y: auto;
}

.footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2xs);
  padding: var(--space-sm) var(--space-md);
  background-color: var(--color-surface-sunken);
  border-top: var(--border-width) solid var(--color-border);
}
</style>
